import { useEffect, useRef, useState } from "react";
import { getPlatformDgSyncConfig, isDgSyncInProgress } from "@/services/dgSync";

const ACTIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 30000;

/** 持续观察平台同步；终态或错过进行中的短任务完成时刷新当前列表。 */
export function usePlatformDgSyncStatus(onCompleted: () => void, enabled = true) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const onCompletedRef = useRef(onCompleted);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let wasSyncing = false;
    let previousCompletion: string | null = null;

    const poll = async () => {
      try {
        const { allUser, hongKong } = await getPlatformDgSyncConfig();
        if (cancelled) return;
        const syncing = isDgSyncInProgress(allUser) || isDgSyncInProgress(hongKong);
        const completion = JSON.stringify([
          allUser.lastSyncCompletedAt,
          hongKong.lastSyncCompletedAt,
        ]);
        const times = [allUser.lastSyncCompletedAt, hongKong.lastSyncCompletedAt]
          .filter((time): time is string => Boolean(time))
          .sort();
        setIsSyncing(syncing);
        setLastSyncTime(times.at(-1) ?? null);
        if (
          !syncing &&
          (wasSyncing || (previousCompletion !== null && completion !== previousCompletion))
        ) {
          onCompletedRef.current();
        }
        wasSyncing = syncing;
        previousCompletion = completion;
      } catch {
        // 暂时读不到状态时保留展示，后续继续读取，避免一次网络失败留下永久 Syncing。
      }
      if (!cancelled) {
        timer = setTimeout(() => void poll(), wasSyncing ? ACTIVE_POLL_MS : IDLE_POLL_MS);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled]);

  return { isSyncing, lastSyncTime };
}
